import React, {useEffect, useState} from "react";
import {BaseGrid, FlexCol, FlexRow} from "./generic/Containers";
import {
    BCHeader1,
    BCHeader2,
    BCHeader3,
    BCHeader4,
    Body1,
    Body2,
    Body3,
    EnhancedPageTitle,
    Subtitle1
} from "./generic/BCHeaders";
import {BCIcon, ICONS} from "./generic/BCIcons";
import ReactMarkdown from "react-markdown";
import {INTERVIEW_QUESTIONS, RESUME_REVIEWER} from "../constants/ToolTypesConfig";
import {ResumeReviewerTool} from "../pages/ToolingSection";
import {useNavigate} from "react-router-dom";
import {getAuth, signOut} from "firebase/auth";
import {STRINGS} from "../constants/Strings";
import {BCPrimaryButton, BCSecondaryButton, BCTertiaryButton} from "./generic/BCButtons";
import {Resource} from "../models/Resource";
import VideoPlayer from "./VideoPlayer";
import {Interview} from "../models/Interview";
import {Course} from "../models/Course";
import "../css/Header.css"
import {DashboardNavBar} from "../pages/DashboardV2";
import {CourseLessonsDashboard} from "./RoadmapContent";
import {ResourcePDF} from "./PDFViewer";
import {useGlobalState} from "../GlobalState";
import {FOOTER_CONFIG} from "../constants/FooterConfig";
import {ToolType} from "../models/ToolType";
import {Article} from "../models/Article";
import {Reference, ReferenceItem} from "../models/Reference";
import {Game, GameItem} from "../models/Game";
import {Challenge, ChallengeItem, AnswerType} from "../models/Challenge";
import "../css/AccountPage.css";
import "../css/Markdown.css";
import {BCTagList, TAGS, Tag, TAG_COLORS} from "./generic/BCTag";
import {ItemsFilter} from "./generic/ItemsFilter";
import {markChallengeItemAsCompleted} from "../util/ProgressTrackingService";
import {hasUserCompletedCourse, getCourseProgress, hasUserCompletedInterview, hasUserCompletedChallengeItem} from "../util/UserProgressHelper";
import {CHALLENGES_TYPES_CONFIG} from "../constants/ChallengesTypesConfig";
import {useParams} from "react-router-dom";
import {markInterviewAsCompleted} from "../util/ProgressTrackingService";
import {trackEvent, TRACKING_EVENTS} from "../util/TrackingHelper";


const STRIPE_BILLING_URL = process.env.REACT_APP_STRIPE_BILLING_URL

export const DashboardContent: React.FC<{page: string, handleBack: any, selectedContent: any, setSelectedContent: any}> = ({page, handleBack, selectedContent, setSelectedContent}) => {

    return (
        <FlexCol padding={2} gap={1} flex={1}>
            <DashboardNavBar onNavClicked={() => handleBack()}/>
            {/*{page === "roadmapSection" && <RoadMapContent selectedContent={selectedContent} setSelectedContent={setSelectedContent}/> }*/}
            {page === "courses" && <CourseContent course={selectedContent} />}
            {page === "videos" && <VideoContent video={selectedContent}/>}
            {page === "tools" && <ToolContent tool={selectedContent}/>}
            {page === "resources" && <ResourceContent resource={selectedContent}/>}
            {page === "references" && <ReferenceContent reference={selectedContent}/>}
            {page === "challenges" && <ChallengeContent challenge={selectedContent}/>}
            {page === "challenge-detail" && <ChallengeDetailContent challenge={selectedContent}/>}
            {page === "games" && <GameContent game={selectedContent}/>}
            {page === "blog" && <BlogContent blog={selectedContent}/>}
        </FlexCol>
    )
}

export const CourseContent: React.FC<{course: Course}> = ({course}) => {
    const { isAuthenticated, userProgressData } = useGlobalState();
    
    if (!course) {
        return <></>;
    }

    // Get course completion status and progress
    const isCompleted = isAuthenticated && course.trackingId 
        ? hasUserCompletedCourse(userProgressData, course.trackingId)
        : false;
    
    const progressPercentage = isAuthenticated && course.trackingId 
        ? getCourseProgress(userProgressData, course.trackingId)
        : 0;

    return (
        <FlexCol gap={.5}>
            <FlexRow justify="space-between" align="center">
                <FlexRow gap={1} align="center">
                    <BCHeader1 text={course.name} />
                    {isCompleted && (
                        <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{
                            color: 'var(--success-color, #28a745)',
                            fontSize: '1.5em'
                        }} />
                    )}
                </FlexRow>
                {progressPercentage > 0 && (
                    <FlexRow gap={0.5} align="center">
                        <Body3 text={`${progressPercentage}% Complete`} style={{
                            color: isCompleted ? 'var(--success-color, #28a745)' : 'var(--primary-blue)',
                            fontWeight: 'bold'
                        }} />
                    </FlexRow>
                )}
            </FlexRow>

            {isCompleted && (
                <FlexRow gap={1} align="center" mBottom={1} style={{
                    backgroundColor: 'var(--success-background, #e8f5e8)',
                    padding: '0.75em',
                    borderRadius: '0.5em',
                    border: '1px solid var(--success-border, #4caf50)'
                }}>
                    <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{ color: 'var(--success-color, #28a745)' }} />
                    <Body2 text="🎉 Course Completed! Great job finishing all lessons." style={{
                        color: 'var(--success-text, #2e7d32)',
                        fontWeight: 'bold'
                    }} />
                </FlexRow>
            )}

            <Subtitle1 text={course.description} />
            
            {progressPercentage > 0 && progressPercentage < 100 && (
                <FlexCol gap={0.5} mBottom={1}>
                    <Body3 text="Your Progress:" style={{ color: 'var(--secondary-text-color)' }} />
                    <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: 'var(--progress-bg, #e9ecef)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${progressPercentage}%`,
                            height: '100%',
                            backgroundColor: 'var(--primary-blue)',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                </FlexCol>
            )}

            <CourseLessonsDashboard course={(course as Course)} />
        </FlexCol>
    );
};

export const VideoContent: React.FC<{video: Interview}> = ({video}) => {
    const { isAuthenticated, userProgressData, user, setUserProgressData } = useGlobalState();
    const [isInterviewCompleting, setIsInterviewCompleting] = useState(false);

    if (!video) {
        return <></>;
    }

    // Check if this interview is already completed
    const isCompleted = isAuthenticated && video.trackingId 
        ? hasUserCompletedInterview(userProgressData, video.trackingId)
        : false;

    // Handle interview completion
    const handleInterviewCompletion = async () => {
        if (!isAuthenticated || !user?.uid || !video.trackingId) return;
        
        // Check if already completed to avoid duplicate saves
        if (isCompleted) return;
        
        setIsInterviewCompleting(true);
        try {
            await markInterviewAsCompleted(user.uid, video.trackingId);
            
            // Update local state immediately for real-time UI updates
            setUserProgressData((currentProgressData: any) => {
                if (!currentProgressData) return currentProgressData;
                
                // Check if already completed to avoid duplicate updates
                const isCompleted = hasUserCompletedInterview(currentProgressData, video.trackingId);
                if (isCompleted) {
                    return currentProgressData;
                }
                
                const updatedProgressData = { ...currentProgressData };
                
                // Ensure interviews array exists
                if (!updatedProgressData.interviews) {
                    updatedProgressData.interviews = [];
                }
                
                // Find or create the interview entry
                let interviewEntry = updatedProgressData.interviews.find((i: any) => i.id === video.trackingId);
                if (!interviewEntry) {
                    interviewEntry = {
                        id: video.trackingId,
                        hasCompleted: false,
                        displayInRoadMap: true
                    };
                    updatedProgressData.interviews.push(interviewEntry);
                }
                
                // Mark interview as completed
                interviewEntry.hasCompleted = true;
                
                return updatedProgressData;
            });
        } catch (error) {
            console.error('Error saving interview completion:', error);
        } finally {
            setIsInterviewCompleting(false);
        }
    };

    return (
        <FlexCol className={"video-content-container"} gap={1}>
            <FlexRow justify="space-between" align="center">
                <FlexRow gap={1} align="center">
                    <BCHeader1 text={video.name} />
                    {isCompleted && (
                        <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{
                            color: 'var(--success-color, #28a745)',
                            fontSize: '1.5em'
                        }} />
                    )}
                </FlexRow>
            </FlexRow>

            <BCHeader3 text={video.description} />
            <VideoPlayer 
                fileUrl={`interviews/${video.fileUrl}`} 
                isPremium={true}
                isAuthenticated={isAuthenticated}
                onVideoComplete={handleInterviewCompletion}
                isCompletingLesson={isInterviewCompleting}
            />
        </FlexCol>
    );
};

export const ResourceContent: React.FC<{resource: Resource}> = ({resource}) => {
    if (!resource) {
        return <></>;
    }
    return (
        <FlexCol>
            <BCHeader1 text={resource.name} />
            <BCHeader3 text={resource.description} />
            <ResourcePDF resource={resource}/>
        </FlexCol>
    );
};

export const BlogContent: React.FC<{blog: Article}> = ({blog}) => {
    if (!blog) {
        return <p>Article not found</p>;
    }

    return (
        <FlexCol gap={1} maxWidth={50} alignSelf={"center"}>
            <img src={blog.thumbnail} alt={`${blog.name} icon`}/>
            <div className="react-markdown">
                <ReactMarkdown>{blog.content}</ReactMarkdown>
            </div>
        </FlexCol>
    );
};

export const ReferenceContent: React.FC<{reference: Reference}> = ({reference}) => {
    const [filteredItems, setFilteredItems] = React.useState<ReferenceItem[]>([]);
    
    if (!reference) {
        return <></>;
    }

    const getAllTags = (): Tag[] => {
        const allTags: Tag[] = [];
        reference.referenceItems.forEach(item => {
            if (item.tags) {
                allTags.push(...item.tags);
            }
        });

        const uniqueTags = allTags.filter((tag, index, self) =>
            index === self.findIndex(t => t.title === tag.title)
        );

        if (reference.trackingId === 'beginnerFriendlyApis') {
            const apiTagOrder = ['Free', 'Freemium', 'Paid', 'No Auth', 'Auth'];
            return uniqueTags.sort((a, b) => {
                const aIndex = apiTagOrder.indexOf(a.title);
                const bIndex = apiTagOrder.indexOf(b.title);
                return aIndex - bIndex;
            });
        } else if (reference.trackingId === 'programmingBookRecommendations') {
            const bookTagOrder = ['Beginner', 'Intermediate', 'Advanced', 'Python', 'All Languages'];
            return uniqueTags.sort((a, b) => {
                const aIndex = bookTagOrder.indexOf(a.title);
                const bIndex = bookTagOrder.indexOf(b.title);
                return aIndex - bIndex;
            });
        }

        return uniqueTags;
    };

    const allTags = getAllTags();

    const getItemTags = (item: ReferenceItem) => item.tags;

    return (
        <FlexCol widthPercent={100}>
            <EnhancedPageTitle title={reference.name} subtitle={reference.description}/>
            <ItemsFilter
                items={reference.referenceItems}
                allTags={allTags}
                getItemTags={getItemTags}
                onFilteredItemsChange={setFilteredItems}
            />
            <BaseGrid gap={1} columns={1} mobileColumns={1} mTop={1} mBottom={2}>
                {filteredItems.map((referenceItem: ReferenceItem, index: number) => (
                    <FlexRow 
                        key={index}
                        className="reference-item-preview" 
                        onClick={() => {
                            // Track detail item click
                            trackEvent(TRACKING_EVENTS.DETAIL_ITEM_CLICKED, {
                                content_type: 'reference',
                                content_name: reference.name,
                                detail_item_type: 'reference_item',
                                detail_item_name: referenceItem.title,
                                detail_item_url: referenceItem.url
                            });
                            window.open(referenceItem.url, "_blank");
                        }}
                    >
                        <img src={referenceItem.image} alt={`${referenceItem.title} icon`}/>
                        <FlexCol padding={1} gap={1}>
                            <BCHeader2 text={referenceItem.title} />
                            <Body1 text={referenceItem.description} />
                            {referenceItem.tags && <BCTagList tags={referenceItem.tags} />}
                        </FlexCol>
                    </FlexRow>
                ))}
            </BaseGrid>
        </FlexCol>
    );
};

export const GameContent: React.FC<{game: Game}> = ({game}) => {
    if (!game) {
        return <></>;
    }
    return (
        <FlexCol widthPercent={100}>
            <EnhancedPageTitle title={game.name} subtitle={game.description}/>
            <BaseGrid gap={1} columns={1} mobileColumns={1} mTop={1} mBottom={2}>
                {game.gameItems.map((gameItem: GameItem, index: number) => (
                    <FlexRow 
                        key={index} 
                        className="reference-item-preview" 
                        onClick={() => {
                            // Track detail item click
                            trackEvent(TRACKING_EVENTS.DETAIL_ITEM_CLICKED, {
                                content_type: 'game',
                                content_name: game.name,
                                detail_item_type: 'game_item',
                                detail_item_name: gameItem.title,
                                detail_item_url: gameItem.url
                            });
                            window.open(gameItem.url, "_blank");
                        }}
                    >
                        <img src={gameItem.image} alt={`${gameItem.title} icon`}/>
                        <FlexCol padding={1} gap={1}>
                            <BCHeader2 text={gameItem.title} />
                            <Body1 text={gameItem.description} />
                        </FlexCol>
                    </FlexRow>
                ))}
            </BaseGrid>
        </FlexCol>
    );
};

export const ChallengeContent: React.FC<{challenge: Challenge}> = ({challenge}) => {
    const navigate = useNavigate();
    const { isAuthenticated, userProgressData } = useGlobalState();
    const [filteredItems, setFilteredItems] = React.useState<ChallengeItem[]>([]);

    if (!challenge) {
        return <></>;
    }

    const getAllTags = (challenge: Challenge): Tag[] => {
        const allTags: Tag[] = [];
        challenge.challengeItems.forEach(item => {
            if (item.tags) {
                allTags.push(...item.tags);
            }
        });
        
        // Remove duplicates by comparing tag titles
        const uniqueTags = allTags.filter((tag, index, self) => 
            index === self.findIndex(t => t.title === tag.title)
        );

        const challengeTagOrder = ['Easy', 'Medium', 'Hard'];
        return uniqueTags.sort((a, b) => {
            const aIndex = challengeTagOrder.indexOf(a.title);
            const bIndex = challengeTagOrder.indexOf(b.title);
            return aIndex - bIndex;
        });
    };

    const allTags = getAllTags(challenge);

    const getItemTags = (item: ChallengeItem) => item.tags;

    const handleChallengeClick = (challengeItem: ChallengeItem) => {
        // Track detail item click
        trackEvent(TRACKING_EVENTS.DETAIL_ITEM_CLICKED, {
            content_type: 'challenge',
            content_name: challenge.name,
            detail_item_type: 'challenge_item',
            detail_item_name: challengeItem.title,
            detail_item_id: challengeItem.id
        });
        
        navigate(`/challenges/${challenge.navUrl}/${challengeItem.id}`);
    };

    return (
        <FlexCol widthPercent={100}>
            <EnhancedPageTitle title={challenge.name} subtitle={challenge.description}/>
            <ItemsFilter
                items={challenge.challengeItems}
                allTags={allTags}
                getItemTags={getItemTags}
                onFilteredItemsChange={setFilteredItems}
            />
            <BaseGrid gap={1} columns={1} mobileColumns={1} mTop={1} mBottom={2}>
                {filteredItems.map((challengeItem: ChallengeItem, index: number) => {
                    // Check if this challenge item is completed
                    const isCompleted = isAuthenticated && challenge.trackingId && challengeItem.id 
                        ? hasUserCompletedChallengeItem(userProgressData, challenge.trackingId, challengeItem.id)
                        : false;
                    
                    return (
                        <FlexRow 
                            key={index} 
                            borderRadius={1} 
                            padding={.5}
                            gap={1} 
                            className="challenge-item-preview" 
                            onClick={() => handleChallengeClick(challengeItem)}
                            style={{
                                border: '1px solid var(--border-default)',
                                backgroundColor: isCompleted ? 'var(--success-background, #f8f9fa)' : 'var(--card-background)',
                                cursor: 'pointer',
                                position: 'relative'
                            }}
                            reverseTablet={true} 
                            reverseMobile={true}
                        >
                            <FlexCol padding={1} gap={1} flex={1}>
                                <FlexRow justify="space-between" align="center">
                                    <FlexRow gap={1} align="center">
                                        <BCHeader3 text={challengeItem.title} />
                                    </FlexRow>
                                    {challengeItem.tags && <BCTagList tags={challengeItem.tags} />}
                                </FlexRow>
                                <Body1 text={challengeItem.previewDescription || challengeItem.description} />
                                {isCompleted && (
                                    <div style={{
                                        alignSelf: 'flex-start',
                                        backgroundColor: 'var(--primary-blue)',
                                        color: 'white',
                                        padding: '0.25em 0.5em',
                                        borderRadius: '0.5em',
                                        fontSize: '0.75em',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25em'
                                    }}>
                                        <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{ fontSize: '0.9em' }} />
                                        <span>Completed</span>
                                    </div>
                                )}
                            </FlexCol>
                        </FlexRow>
                    );
                })}
            </BaseGrid>
        </FlexCol>
    );
};

export const ChallengeDetailContent: React.FC<{challenge: any}> = ({challenge}) => {
    const { contentId } = useParams();
    const { isAuthenticated, setShowAuthModal, setAuthModalMode, userProgressData, user, setUserProgressData } = useGlobalState();
    const [userAnswer, setUserAnswer] = useState<string | number | ''>('');
    const [revealedHintsCount, setRevealedHintsCount] = useState(0);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    if (!challenge) {
        return <></>;
    }

    // Get the parent challenge info for progress tracking
    const parentChallenge = CHALLENGES_TYPES_CONFIG.find(ch => ch.navUrl === contentId);
    const challengeTrackingId = parentChallenge?.trackingId;
    const challengeItemId = challenge.id;

    // Check if this challenge is already completed
    const isAlreadyCompleted = isAuthenticated && challengeTrackingId && challengeItemId 
        ? hasUserCompletedChallengeItem(userProgressData, challengeTrackingId, challengeItemId)
        : false;

    const handleSubmitAnswer = async () => {
        // Check if user is authenticated before allowing answer submission
        if (!isAuthenticated) {
            setAuthModalMode('register');
            setShowAuthModal(true);
            return;
        }

        if (userAnswer !== '' && userAnswer !== null && userAnswer !== undefined) {
            setHasSubmitted(true);
            
            // Compare answers based on answer type
            let correct: boolean;
            if (challenge.answerType === AnswerType.NUMBER) {
                // For number answers, convert both to numbers for comparison
                correct = Number(userAnswer) === Number(challenge.solution);
            } else {
                // For string answers, compare as strings (case-sensitive)
                correct = String(userAnswer).trim() === String(challenge.solution).trim();
            }
            
            setIsCorrect(correct);
            
            // If correct and not already completed, save progress
            if (correct && !isAlreadyCompleted && challengeTrackingId && challengeItemId && user?.uid) {
                setIsSaving(true);
                try {
                    await markChallengeItemAsCompleted(user.uid, challengeTrackingId, challengeItemId);
                    
                    // Track challenge completion event
                    trackEvent(TRACKING_EVENTS.CHALLENGE_COMPLETED, {
                        challenge_id: challengeTrackingId,
                        challenge_name: challenge.title,
                        challenge_item_id: challengeItemId,
                        challenge_item_title: challenge.title,
                        user_id: user.uid
                    });
                    
                    // Update local state immediately for real-time UI updates
                    if (userProgressData && setUserProgressData) {
                        const updatedProgressData = { ...userProgressData };
                        
                        // Ensure challenges array exists
                        if (!updatedProgressData.challenges) {
                            updatedProgressData.challenges = [];
                        }
                        
                        // Find or create the challenge entry
                        let challengeEntry = updatedProgressData.challenges.find((c: any) => c.id === challengeTrackingId);
                        if (!challengeEntry) {
                            challengeEntry = {
                                id: challengeTrackingId,
                                hasCompleted: false,
                                displayInRoadMap: true,
                                challengeItems: []
                            };
                            updatedProgressData.challenges.push(challengeEntry);
                        }
                        
                        // Find or create the challenge item entry
                        let challengeItemEntry = challengeEntry.challengeItems.find((item: any) => item.id === challengeItemId);
                        if (!challengeItemEntry) {
                            challengeItemEntry = {
                                id: challengeItemId,
                                hasCompleted: false
                            };
                            challengeEntry.challengeItems.push(challengeItemEntry);
                        }
                        
                        // Mark as completed
                        challengeItemEntry.hasCompleted = true;
                        
                        // Check if all challenge items are completed to mark entire challenge as complete
                        const allItemsComplete = challengeEntry.challengeItems.every((item: any) => item.hasCompleted);
                        if (allItemsComplete) {
                            challengeEntry.hasCompleted = true;
                        }
                        
                        // Update the global state
                        setUserProgressData(updatedProgressData);
                    }
                } catch (error) {
                    console.error('Error saving challenge completion:', error);
                } finally {
                    setIsSaving(false);
                }
            }
            
        }
    };

    const handleShowNextHint = () => {
        setRevealedHintsCount(prev => prev + 1);
    };

    return (
        <FlexCol widthPercent={100}>
            <FlexRow justify="space-between" align="center" mBottom={1}>
                <FlexRow gap={1} align="center">
                    <BCHeader1 text={challenge.title} />
                    {(isAlreadyCompleted || (hasSubmitted && isCorrect)) && (
                        <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{ color: 'var(--color-primary)', fontSize: '1.5em' }} />
                    )}
                </FlexRow>
                {challenge.tags && <BCTagList tags={challenge.tags} />}
            </FlexRow>
            
            <FlexCol gap={0.5} style={{ marginBottom: '2em' }}>
                <BCHeader3 text="Description" />
                <Body1 text={challenge.description} style={{
                    whiteSpace: 'pre-line',
                    backgroundColor: 'var(--description-background, #f8f9fa)',
                    padding: '1em',
                    borderRadius: '0.5em',
                    borderLeft: '4px solid var(--primary-gray, #6c757d)',
                    lineHeight: '1.6'
                }} />
            </FlexCol>

            {challenge.exampleInput && challenge.exampleOutput && (
                <FlexCol gap={1.5} style={{ marginBottom: '2em' }}>
                    <FlexRow gap={2} reverseMobile={true}>
                        <FlexCol flex={1} gap={0.5}>
                            <BCHeader4 text="Example Input:" />
                            <Body2 text={challenge.exampleInput} style={{
                                fontFamily: 'monospace',
                                backgroundColor: 'var(--input-background, #e8f4f8)',
                                padding: '0.75em',
                                borderRadius: '0.25em',
                                borderLeft: '4px solid var(--primary-cyan, #17a2b8)'
                            }} />
                        </FlexCol>
                        <FlexCol flex={1} gap={0.5}>
                            <BCHeader4 text="Expected Output:" />
                            <Body2 text={challenge.exampleOutput} style={{
                                fontFamily: 'monospace',
                                backgroundColor: 'var(--output-background, #e8f8e8)',
                                padding: '0.75em',
                                borderRadius: '0.25em',
                                borderLeft: '4px solid var(--primary-green, #28a745)'
                            }} />
                        </FlexCol>
                    </FlexRow>
                    
                    {challenge.exampleExplanation && (
                        <FlexCol gap={0.5}>
                            <BCHeader4 text="Explanation:" />
                            <Body2 text={challenge.exampleExplanation} style={{
                                fontStyle: 'italic',
                                fontFamily: 'monospace',
                                backgroundColor: 'var(--explanation-background, #f8f9fa)',
                                padding: '0.75em',
                                borderRadius: '0.25em',
                                borderLeft: '4px solid var(--primary-blue, #007bff)',
                                color: 'var(--secondary-text-color, #6c757d)'
                            }} />
                        </FlexCol>
                    )}
                </FlexCol>
            )}

            <FlexCol gap={0.5} style={{ marginBottom: '2em' }}>
                <BCHeader3 text="Problem:" />
                <Body1 text={challenge.problem || challenge.problemStatement} style={{
                    whiteSpace: 'pre-line',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    backgroundColor: 'var(--problem-background, #fffbf0)',
                    padding: '1em',
                    borderRadius: '0.5em',
                    borderLeft: '4px solid var(--primary-yellow, #ffc107)',
                    fontWeight: '500'
                }} />
            </FlexCol>

            {/* Help Section */}
            <FlexRow gap={1} style={{ marginBottom: '2em' }}>
                {challenge.hints && challenge.hints.length > 0 && revealedHintsCount < challenge.hints.length && (
                    <BCTertiaryButton
                        text={revealedHintsCount === 0 ? "Show First Hint" : "Show Next Hint"}
                        onClick={handleShowNextHint}
                        iconLeft={ICONS.SPARKLE}
                    />
                )}
            </FlexRow>

            {/* Hints Section */}
            {revealedHintsCount > 0 && challenge.hints && challenge.hints.length > 0 && (
                <FlexCol gap={0.5} style={{ marginBottom: '2em' }}>
                    <BCHeader4 text="Hints:" />
                    {challenge.hints.slice(0, revealedHintsCount).map((hint: string, hintIndex: number) => (
                        <Body2 key={hintIndex} text={`${hintIndex + 1}. ${hint}`} style={{
                            fontStyle: 'italic',
                            color: 'var(--secondary-text-color)'
                        }} />
                    ))}
                </FlexCol>
            )}

            {/* User Answer Section */}
            <FlexCol gap={1} mBottom={2} >
                <BCHeader3 text="Your Answer"/>
                <FlexRow gap={1} align="center">
                    <input
                        type={challenge.answerType === AnswerType.NUMBER ? "number" : "text"}
                        value={userAnswer}
                        onChange={(e) => {
                            if (challenge.answerType === AnswerType.NUMBER) {
                                setUserAnswer(e.target.value === '' ? '' : parseInt(e.target.value));
                            } else {
                                setUserAnswer(e.target.value);
                            }
                        }}
                        className="challenge-input-clean"
                        style={{
                            width: '100%',
                            maxWidth: '30em',
                            padding: '0.75em',
                            fontSize: '1.1em',
                            border: '1px solid var(--color-gray-300)',
                            borderRadius: '0.5em',
                            backgroundColor: 'var(--background-container-default, white)'
                        }}
                        onWheel={(e) => challenge.answerType === AnswerType.NUMBER ? e.currentTarget.blur() : undefined}
                        disabled={isAlreadyCompleted}
                    />
                    
                    <BCPrimaryButton 
                        text={!isAuthenticated ? "Sign Up to Submit Answer" : isAlreadyCompleted ? "Already Completed" : isSaving ? "Saving..." : "Submit"}
                        onClick={handleSubmitAnswer}
                        disabled={isAlreadyCompleted || isSaving || (isAuthenticated && (userAnswer === '' || userAnswer === null || userAnswer === undefined))}
                    />

                </FlexRow>
                
                {!isAuthenticated && (
                    <Body2 text="Sign up for free to submit answers and track your progress!" style={{
                        color: 'var(--primary-blue)',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        marginTop: '0.5em',
                        maxWidth: '30em',
                        alignSelf: 'flex-start'
                    }} />
                )}

                {hasSubmitted && isCorrect === true && !isAlreadyCompleted && (
                    <FlexRow gap={1} align="center" style={{ maxWidth: '30em' }}>
                        <BCIcon icon={ICONS.CHECKMARK_CIRCLE} style={{ color: 'green' }} />
                        <Body2 text="Correct! Great job!" style={{
                            color: 'green',
                            fontWeight: 'bold'
                        }} />
                        {isSaving && <Body2 text="Saving progress..." style={{ color: 'var(--secondary-text-color)' }} />}
                    </FlexRow>
                )}

                {hasSubmitted && isCorrect === false && (
                    <Body2 text="Incorrect - Try again!" style={{
                        color: '#e74c3c',
                        fontWeight: 'bold',
                        maxWidth: '30em',
                    }} />
                )}
            </FlexCol>
        </FlexCol>
    );
};

export const ToolContent: React.FC<{tool: ToolType}> = ({tool}) => {
    if (!tool) {
        return <></>;
    }
    return (
        <FlexCol widthPercent={100} gap={1}>
            <FlexCol widthPercent={100}>
                <BCHeader1 text={tool.name} />
                <BCHeader3 text={tool.description} />
            </FlexCol>
            {tool.type === RESUME_REVIEWER && <ResumeReviewerTool toolType={tool}/>}
            {tool.type === INTERVIEW_QUESTIONS && <ResumeReviewerTool toolType={tool}/>}
        </FlexCol>
    );
};

export const AccountContent: React.FC<any> = ({}) => {
    const navigate = useNavigate();

    const handleLogout = () => {
        const auth = getAuth();
        signOut(auth)
            .then(() => {
                navigate("/")
            })
            .catch((error) => {
                console.error("Error signing out: ", error);
            });
    };

    return (
        <FlexCol 
            gap={2} 
            maxWidth={25} 
            justify={"center"} 
            alignSelf={"center"} 
            heightPercent={100}
            padding={2}
        >

            {/* Action Buttons */}
            <FlexCol gap={1.5} widthPercent={100}>
                <BCPrimaryButton 
                    text={STRINGS.ACCOUNT_MANAGE_CTA} 
                    onClick={() => window.open(STRIPE_BILLING_URL, "_blank")}
                    className="account-action-btn"
                    style={{
                        background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-600) 100%)',
                        boxShadow: '0 6px 16px rgba(50, 119, 193, 0.25)',
                        border: 'none',
                        fontSize: '1.1em',
                        fontWeight: '600',
                        padding: '1em 2em',
                        transition: 'all 0.3s ease',
                        transform: 'translateY(0)'
                    }}
                />
                <BCSecondaryButton 
                    text={STRINGS.ACCOUNT_LOGOUT_CTA} 
                    onClick={handleLogout}
                    className="account-action-btn"
                    style={{
                        background: 'transparent',
                        border: '2px solid var(--color-primary-300)',
                        color: 'var(--color-primary-700)',
                        fontSize: '1.1em',
                        fontWeight: '600',
                        padding: '1em 2em',
                        transition: 'all 0.3s ease',
                        transform: 'translateY(0)'
                    }}
                />
            </FlexCol>
        </FlexCol>
    )
}

export const LegalContent: React.FC = () => {
    return (
        <FlexCol alignSelf={"center"} gap={1} heightPercent={100}>
            <FlexRow gap={2} justify={"center"}>
                <a href={FOOTER_CONFIG.privacyUrl}>
                    <Body1 text={FOOTER_CONFIG.privacyText}/>
                </a>
                <a href={FOOTER_CONFIG.termsUrl}>
                    <Body1 text={FOOTER_CONFIG.termsText}/>
                </a>
            </FlexRow>
            <Body1 text={FOOTER_CONFIG.allRights} textAlign={"center"}/>
        </FlexCol>
    )
}

